/**
 * OrderDesk dated-folder load with the same bridges as Packers / Production:
 * membership folder + Wholesale (Sent to Xero) + Wholesale landing (pending).
 *
 * Printing / Packers / planner desks should use this instead of membership-only
 * when operators expect the full week desk.
 */

import {
  fetchOrderDeskFolderOrders,
} from './orderDeskFolderOrdersService.js';
import {
  fetchXeroLinkedOrdersForSelectedFolder,
  getOrderBridgeKeys,
  XERO_WHOLESALE_FOLDER_NAME,
} from './xeroWholesaleFolderBridge.js';
import {
  fetchWholesalePendingForSelectedFolder,
  WHOLESALE_LANDING_FOLDER_NAME,
} from './wholesalePendingFolderBridge.js';

function ensureItemAliases(order) {
  if (!order || typeof order !== 'object') return order;
  if (!Array.isArray(order.items) && Array.isArray(order.order_items)) {
    order.items = order.order_items;
  }
  if (!Array.isArray(order.order_items) && Array.isArray(order.items)) {
    order.order_items = order.items;
  }
  return order;
}

function collectBridgeKeys(orders) {
  const existingKeys = new Set();
  (orders || []).forEach((order) => {
    getOrderBridgeKeys(order).forEach((key) => existingKeys.add(key));
  });
  return existingKeys;
}

/**
 * Append bridge orders that are not already present (by Packers identity keys).
 * @param {object[]} base
 * @param {object[]} linked
 * @returns {{ orders: object[], added: number }}
 */
export function mergeBridgedOrdersIntoFolderList(base, linked) {
  const orders = Array.isArray(base) ? [...base] : [];
  const existingKeys = collectBridgeKeys(orders);
  let added = 0;
  for (const raw of linked || []) {
    const order = ensureItemAliases({ ...raw });
    const keys = getOrderBridgeKeys(order);
    if (!keys.length) continue;
    if (!keys.every((key) => !existingKeys.has(key))) continue;
    keys.forEach((key) => existingKeys.add(key));
    orders.push(order);
    added += 1;
  }
  return { orders, added };
}

/**
 * @param {{
 *   folder: { id?: *, name?: string }|null,
 *   folders?: Array<{ id?: *, name?: string }>,
 *   scope?: 'membership'|'dateField',
 *   dateField?: 'delivery'|'dispatch',
 *   includeUndatedWholesalePending?: boolean,
 * }} args
 * @returns {Promise<{
 *   orders: object[],
 *   membershipCount: number,
 *   xeroLinkedCount: number,
 *   pendingLinkedCount: number,
 *   undatedPendingOrders: object[],
 *   notes: string[],
 *   skippedReasons: string[],
 * }>}
 */
export async function fetchOrderDeskFolderOrdersWithBridges({
  folder,
  folders = [],
  scope = 'membership',
  dateField = 'delivery',
  includeUndatedWholesalePending = false,
} = {}) {
  if (!folder) {
    return {
      orders: [],
      membershipCount: 0,
      xeroLinkedCount: 0,
      pendingLinkedCount: 0,
      undatedPendingOrders: [],
      notes: [],
      skippedReasons: ['No folder selected.'],
    };
  }

  const membershipOrders = await fetchOrderDeskFolderOrders({
    folder,
    scope,
    dateField,
  });
  let orders = (membershipOrders || []).map((order) => ensureItemAliases({ ...order }));
  const membershipCount = orders.length;
  const notes = [];
  const skippedReasons = [];

  let xeroLinkedCount = 0;
  try {
    const { linkedOrders, skippedReason } = await fetchXeroLinkedOrdersForSelectedFolder({
      selectedFolder: folder,
      folders,
    });
    if (skippedReason) skippedReasons.push(skippedReason);
    const merged = mergeBridgedOrdersIntoFolderList(orders, linkedOrders);
    orders = merged.orders;
    xeroLinkedCount = merged.added;
    if (xeroLinkedCount) {
      notes.push(`${xeroLinkedCount} from ${XERO_WHOLESALE_FOLDER_NAME}`);
    }
  } catch (error) {
    console.warn('[fetchOrderDeskFolderOrdersWithBridges] Xero bridge failed:', error);
    skippedReasons.push('Xero bridge failed.');
  }

  let pendingLinkedCount = 0;
  let undatedPendingOrders = [];
  try {
    const {
      linkedOrders: pendingLinked,
      undatedOrders: pendingUndated,
      skippedReason: pendingSkip,
    } = await fetchWholesalePendingForSelectedFolder({
      selectedFolder: folder,
      folders,
    });
    if (pendingSkip) skippedReasons.push(pendingSkip);
    const mergedPending = mergeBridgedOrdersIntoFolderList(orders, pendingLinked);
    orders = mergedPending.orders;
    pendingLinkedCount = mergedPending.added;
    if (pendingLinkedCount) {
      notes.push(`${pendingLinkedCount} pending from ${WHOLESALE_LANDING_FOLDER_NAME}`);
    }
    if (includeUndatedWholesalePending) {
      const undatedMerged = mergeBridgedOrdersIntoFolderList([], pendingUndated);
      undatedPendingOrders = undatedMerged.orders;
      if (undatedPendingOrders.length) {
        notes.push(
          `${undatedPendingOrders.length} undated in ${WHOLESALE_LANDING_FOLDER_NAME}`,
        );
      }
    }
  } catch (error) {
    console.warn('[fetchOrderDeskFolderOrdersWithBridges] Wholesale pending bridge failed:', error);
    skippedReasons.push('Wholesale pending bridge failed.');
  }

  return {
    orders,
    membershipCount,
    xeroLinkedCount,
    pendingLinkedCount,
    undatedPendingOrders,
    notes,
    skippedReasons,
  };
}
